export type RandomFn = () => number;

/**
 * A random integer generator using the improved Mulberry32 algorithm.
 *
 * @param seed - An integer seed value.
 * @returns A function that generates random numbers between [0, 2**32) when called successively.
 */
export function randomGenUintM32(seed: number): RandomFn {
    seed = (seed + 0x9e3779b9) | 0;
    let z = seed;
    return () => {
        z ^= z >>> 16;
        z = Math.imul(z, 0x21f0aaad);
        z ^= z >>> 15;
        z = Math.imul(z, 0x735a2d97);
        z ^= z >>> 15;
        // Convert to unsigned integer.
        return z >>> 0;
    };
}

export function randomUint32ToFloat(int: number) {
    return int / 0x100000000;
}

export function randomUint32ToUintRange(int: number, max: number) {
    // Do not use modulo since it is not uniformly distributed.
    return Math.floor(randomUint32ToFloat(int) * max);
}

/**
 * A random float generator using the improved Mulberry32 algorithm.
 *
 * @param seed - An integer seed value.
 * @returns A function that generates random numbers between [0.0, 1.0) when called successively.
 */
export function makeRandomGenFloatM32(seed: number): RandomFn {
    const intGen = randomGenUintM32(seed);
    return () => randomUint32ToFloat(intGen());
}

/** @see {@link makeRandomGenFloatM32} */
export let globalRandom = /*#__PURE__*/ makeRandomGenFloatM32(0);

export function randomInt(max: number) {
    return Math.floor(globalRandom() * max);
}

export function resetGlobalRng() {
    globalRandom = makeRandomGenFloatM32(0);
}

export function withGlobalSeed<T>(seed: number, fn: () => T) {
    const prevRandom = globalRandom;
    globalRandom = makeRandomGenFloatM32(seed);
    try {
        return fn();
    } finally {
        globalRandom = prevRandom;
    }
}
